import React, { useEffect, useState } from "react";
import {
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  Selection,
  IColumn,
  IDetailsRowProps,
  CheckboxVisibility,
} from "@fluentui/react/lib/DetailsList";
import {
  Stack,
  Dropdown,
  IDropdownOption,
  Text,
  IconButton,
} from "@fluentui/react";

export interface DataGridColumn extends IColumn {}

const customStyles = {
  root: {
    selectors: {
      ".ms-DetailsRow": {
        minHeight: 45,
        height: 45,
      },
    },
  },
};

interface Props {
  items: any[];
  columns: DataGridColumn[];
  pageSize?: number;
  onPageSizeChange?: (pageSize: number) => void;
  onPageChange?: (page: number) => void;
  onRenderRow?: (
    props?: IDetailsRowProps,
    defaultRender?: (props?: IDetailsRowProps) => JSX.Element | null
  ) => JSX.Element | null;
}

export const DataGrid = ({
  items: sourceItems,
  columns,
  pageSize = 10,
  onPageSizeChange,
  onPageChange,
  onRenderRow,
}: Props) => {
  const [isDataRefreshing, setIsDataRefreshing] = useState<boolean>(false);
  const [items, setItems] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Ensure sourceItems is always an array
  const safeSourceItems = Array.isArray(sourceItems) ? sourceItems : [];
  const totalPages = Math.ceil(safeSourceItems.length / pageSize);

  useEffect(() => {
    setIsDataRefreshing(true);
    setCurrentPage(1); // Reset to first page when data changes
  }, [sourceItems]);

  useEffect(() => {
    const start = (currentPage - 1) * pageSize;
    const pageItems = safeSourceItems
      .slice(start, start + pageSize)
      .map((item) => ({ ...item, key: item.id || item.Id || item.key }));
    setItems(pageItems);
    setIsDataRefreshing(false);
  }, [safeSourceItems, currentPage, pageSize]);

  useEffect(() => {
    if (onPageChange) {
      onPageChange(currentPage);
    }
  }, [currentPage, onPageChange]);

  const pageSizeOptions: IDropdownOption[] = [
    { key: 10, text: "10" },
    { key: 25, text: "25" },
    { key: 50, text: "50" },
  ];

  const getPaginationNumbers = () => {
    const maxVisiblePages = 5;
    const pages = [];

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage <= 3) {
        // Show pages 1, 2, 3, 4, ..., last
        pages.push(2, 3, 4);
        if (totalPages > 4) {
          pages.push("...");
          pages.push(totalPages);
        }
      } else if (currentPage >= totalPages - 2) {
        // Show pages 1, ..., last-3, last-2, last-1, last
        if (totalPages > 4) {
          pages.push("...");
        }
        for (let i = totalPages - 3; i <= totalPages; i++) {
          if (i > 1 && !pages.includes(i)) {
            pages.push(i);
          }
        }
      } else {
        // Show pages 1, ..., current-1, current, current+1, ..., last
        pages.push("...");
        pages.push(currentPage - 1, currentPage, currentPage + 1);
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const renderPagination = () => {
    if (totalPages <= 1 && !onPageSizeChange) return null;

    const paginationNumbers = getPaginationNumbers();

    return (
      <Stack
        horizontal
        horizontalAlign="end"
        verticalAlign="center"
        tokens={{ childrenGap: 16 }}
        styles={{ root: { marginTop: 16 } }}
      >
        {/* Results per page dropdown */}
        {onPageSizeChange && (
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Text variant="medium">Results per page</Text>
            <Dropdown
              options={pageSizeOptions}
              selectedKey={pageSize}
              onChange={(_, option) => {
                if (option) {
                  onPageSizeChange(option.key as number);
                  setCurrentPage(1); // Reset to first page when changing page size
                }
              }}
              styles={{
                dropdown: { width: 70 },
                title: { border: "1px solid #ccc", height: 32 },
              }}
            />
          </Stack>
        )}

        {/* Page navigation */}
        {totalPages > 1 && (
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
            {/* Previous button */}
            <IconButton
              iconProps={{ iconName: "ChevronLeft" }}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
              styles={{
                root: {
                  color: currentPage === 1 ? "#a19f9d" : "#323130",
                  backgroundColor: "transparent",
                  border: "1px solid #ccc",
                  minWidth: 32,
                  height: 32,
                },
                rootHovered: {
                  backgroundColor:
                    currentPage === 1 ? "transparent" : "#f3f2f1",
                },
                icon: { fontSize: 12 },
              }}
            />

            {/* Page numbers */}
            {paginationNumbers.map((page, index) => (
              <React.Fragment key={index}>
                {page === "..." ? (
                  <Text
                    variant="medium"
                    styles={{
                      root: {
                        padding: "6px 8px",
                        color: "#605e5c",
                        minWidth: 32,
                        textAlign: "center",
                      },
                    }}
                  >
                    ...
                  </Text>
                ) : (
                  <button
                    onClick={() => setCurrentPage(page as number)}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #ccc",
                      backgroundColor:
                        currentPage === page ? "#0078d4" : "#f3f2f1",
                      color: currentPage === page ? "white" : "#323130",
                      cursor: "pointer",
                      minWidth: 32,
                      height: 32,
                      borderRadius: 2,
                    }}
                  >
                    {page}
                  </button>
                )}
              </React.Fragment>
            ))}

            {/* Next button */}
            <IconButton
              iconProps={{ iconName: "ChevronRight" }}
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
              styles={{
                root: {
                  color: currentPage === totalPages ? "#a19f9d" : "#323130",
                  backgroundColor: "transparent",
                  border: "1px solid #ccc",
                  minWidth: 32,
                  height: 32,
                },
                rootHovered: {
                  backgroundColor:
                    currentPage === totalPages ? "transparent" : "#f3f2f1",
                },
                icon: { fontSize: 12 },
              }}
            />
          </Stack>
        )}
      </Stack>
    );
  };

  return (
    <>
      {isDataRefreshing ? null : (
        <>
          <DetailsList
            items={items}
            compact={false}
            columns={columns}
            selectionMode={SelectionMode.single}
            layoutMode={DetailsListLayoutMode.justified}
            checkboxVisibility={CheckboxVisibility.hidden}
            isHeaderVisible={true}
            styles={customStyles}
            onRenderRow={onRenderRow}
          />
          {renderPagination()}
        </>
      )}
    </>
  );
};

interface MultipleSelectProps {
  items: any[];
  columns: DataGridColumn[];
  onSelectionChanged?: (items: any[]) => any;
}

export const MultipleSelectDataGrid = (props: MultipleSelectProps) => {
  const selection = new Selection({
    onSelectionChanged: () => {
      if (props.onSelectionChanged && selection) {
        props.onSelectionChanged(selection.getSelection());
      }
    },
  });

  return (
    <>
      <DetailsList
        items={props.items}
        compact={false}
        columns={props.columns}
        selectionMode={SelectionMode.multiple}
        selection={selection}
        layoutMode={DetailsListLayoutMode.justified}
        checkboxVisibility={CheckboxVisibility.onHover}
        isHeaderVisible={true}
        styles={customStyles}
      />
    </>
  );
};
